/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Lease detail — /real-estate/leases/:id
 * GET/PUT /re/leases/{id} (embeds unit, parties, rent_schedule, commissions, previous_lease_id),
 * POST /renew, /terminate, PUT /re/rent-schedule/{id}.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, Skeleton, TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import InfoCard from './components/ReInfoCard';
import UserLink from 'components/v2/UserLink';
import { usePermission } from 'auth/usePermission';
import RePage, { LoadError, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { DateField, KV, Num } from './components/Widgets';
import ChequeDialog, { nextChequeActions } from './components/ChequeDialog';
import { RenewLeaseDialog, TerminateLeaseDialog } from './components/LeaseDialogs';
import {
  contactLabel, errorMessage, formatDate, formatMoney, humanize, round2, toNumber, useReApi, useReGet,
} from './api';
import { defaultCurrency, isAustralia } from '../variant';

function EjariDialog({ lease, open, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (open && lease) {
      setForm({
        ...(isAustralia()
          ? {
            bond_lodgement_no: lease.bond_lodgement_no || '', bond_authority: lease.bond_authority || '',
            condition_report_at: lease.condition_report_at || '',
          }
          : { ejari_no: lease.ejari_no || '', ejari_registered_date: lease.ejari_registered_date || '' }),
        move_in_date: lease.move_in_date || '', move_out_date: lease.move_out_date || '', notes: lease.notes || '',
      });
      setErr(null);
    }
  }, [open, lease]);
  const submit = async () => {
    const body = {};
    Object.entries(form).forEach(([k, v]) => { if ((lease[k] || '') !== v) body[k] = v || null; });
    try {
      if (Object.keys(body).length) await api.put(`/re/leases/${lease.id}`, body);
      onDone();
    } catch (e) {
      setErr(errorMessage(e, 'Could not save'));
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isAustralia() ? 'Bond, move-in & notes' : <>Ejari, move-in &amp; notes</>}</DialogTitle>
      <DialogContent>
        {err && <MDTypography variant="caption" color="error">{err}</MDTypography>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {isAustralia() ? (
            <>
              <Grid item xs={6}><TextField fullWidth size="small" label="Bond lodgement no." value={form.bond_lodgement_no || ''} onChange={(e) => setForm({ ...form, bond_lodgement_no: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Bond lodged with" value={form.bond_authority || ''} onChange={(e) => setForm({ ...form, bond_authority: e.target.value })} /></Grid>
              <Grid item xs={6}><DateField label="Condition report" value={form.condition_report_at} onChange={(v) => setForm({ ...form, condition_report_at: v || '' })} sx={{ width: '100%' }} /></Grid>
            </>
          ) : (
            <>
              <Grid item xs={6}><TextField fullWidth size="small" label="Ejari no." value={form.ejari_no || ''} onChange={(e) => setForm({ ...form, ejari_no: e.target.value })} /></Grid>
              <Grid item xs={6}><DateField label="Ejari registered" value={form.ejari_registered_date} onChange={(v) => setForm({ ...form, ejari_registered_date: v || '' })} sx={{ width: '100%' }} /></Grid>
            </>
          )}
          <Grid item xs={6}><DateField label="Move-in" value={form.move_in_date} onChange={(v) => setForm({ ...form, move_in_date: v || '' })} sx={{ width: '100%' }} /></Grid>
          <Grid item xs={6}><DateField label="Move-out" value={form.move_out_date} onChange={(v) => setForm({ ...form, move_out_date: v || '' })} sx={{ width: '100%' }} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline minRows={3} size="small" label="Notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit}>Save</MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default function LeaseDetail() {
  const { id } = useParams();
  const au = isAustralia();
  const navigate = useNavigate();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canEdit } = usePermission('re_leases');
  const { data: l, loading, error, reload } = useReGet(`/re/leases/${id}`);
  const [cheque, setCheque] = useState(null);
  const [target, setTarget] = useState(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [ejariOpen, setEjariOpen] = useState(false);

  const schedule = useMemo(() => [...(l?.rent_schedule || [])].sort((a, b) => a.sequence - b.sequence), [l]);
  const totals = schedule.reduce((a, c) => ({
    all: a.all + (c.status === 'CANCELLED' ? 0 : toNumber(c.amount) || 0),
    cleared: a.cleared + (c.status === 'CLEARED' ? toNumber(c.amount) || 0 : 0),
  }), { all: 0, cleared: 0 });

  const activate = async () => {
    try {
      await api.put(`/re/leases/${id}`, { status: 'ACTIVE' });
      notify('Lease activated');
      reload();
    } catch (e) {
      notify.error(e, 'Could not activate the lease');
    }
  };

  if (loading && !l) return <RePage title="Lease" onBack={() => navigate('/real-estate/leases')}><Skeleton variant="rectangular" height={300} /></RePage>;
  if (!l) return <RePage title="Lease" onBack={() => navigate('/real-estate/leases')}><LoadError error={error} onRetry={reload} /></RePage>;

  const cur = l.currency || defaultCurrency();
  const live = ['ACTIVE', 'RENEWAL_DUE'].includes(l.status);

  return (
    <RePage
      title={l.lease_number || `Lease #${l.id}`}
      subtitle={`${l.unit?.unit_number || l.unit_number || ''} · ${l.unit?.project_name || l.project_name || ''} · ${formatDate(l.start_date)} – ${formatDate(l.end_date)}`}
      onBack={() => navigate('/real-estate/leases')}
      actions={(
        <>
          <ReStatusChip vocab="lease" status={l.status} />
          {canEdit && l.status === 'DRAFT' && <MDButton size="small" variant="gradient" color="success" onClick={activate}>Activate</MDButton>}
          {canEdit && <MDButton size="small" variant="outlined" color="dark" onClick={() => setEjariOpen(true)}>{au ? 'Bond / notes' : 'Ejari / notes'}</MDButton>}
          {canEdit && live && <MDButton size="small" variant="gradient" color="info" onClick={() => setRenewOpen(true)}>Renew</MDButton>}
          {canEdit && live && <MDButton size="small" variant="outlined" color="error" onClick={() => setTermOpen(true)}>Terminate</MDButton>}
        </>
      )}
    >
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <InfoCard icon="groups" title="Parties">
            {(l.parties || []).map((p) => (
              <MDBox key={p.id || `${p.contact_id}-${p.role}`} display="flex" justifyContent="space-between" mb={1}>
                <Link component="button" variant="button" onClick={() => navigate(`/contacts/${p.contact_id}`)} sx={{ fontWeight: 600 }}>
                  {p.contact_name || contactLabel(p.contact) || `Contact #${p.contact_id}`}
                </Link>
                <MDTypography variant="caption" color="text">{humanize(p.role)}</MDTypography>
              </MDBox>
            ))}
            <KV label="Broker" value={l.broker_firm_name ? `${l.broker_firm_name}${l.broker_agent_name ? ` · ${l.broker_agent_name}` : ''}` : '—'} />
            <KV label="Leasing agent" value={l.assigned_to_name ? <UserLink userId={l.assigned_to} name={l.assigned_to_name} tone="link" /> : '—'} />
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard icon="description" title="Terms">
            <KV label="Annual rent" value={formatMoney(l.annual_rent, cur)} bold />
            {au ? (
              <>
                <KV label="Payment" value={`${humanize(l.rent_frequency)} rent · ${formatMoney(round2((toNumber(l.annual_rent) || 0) / 52), cur)} a week`} />
                <KV label="Bond" value={formatMoney(l.bond_amount, cur)} />
              </>
            ) : (
              <>
                <KV label="Payment" value={`${l.number_of_cheques} cheque${l.number_of_cheques === 1 ? '' : 's'} · ${humanize(l.rent_frequency)}`} />
                <KV label="Security deposit" value={`${formatMoney(l.security_deposit, cur)}${l.security_deposit_refunded ? ' (refunded)' : ''}`} />
              </>
            )}
            <KV label="Agency fee" value={formatMoney(l.agency_fee_amount, cur)} />
            <KV label="Term" value={`${formatDate(l.start_date)} – ${formatDate(l.end_date)}`} />
            {l.termination_reason && <KV label="Terminated" value={l.termination_reason} />}
            {l.previous_lease_id && (
              <KV label="Renewal of" value={<Link component="button" variant="button" onClick={() => navigate(`/real-estate/leases/${l.previous_lease_id}`)}>{`Lease #${l.previous_lease_id}`}</Link>} />
            )}
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard icon="verified" title={au ? 'Bond & occupancy' : 'Ejari & occupancy'}>
            {au ? (
              <>
                <KV label="Bond lodged with" value={l.bond_authority || '—'} />
                <KV label="Lodgement no." value={l.bond_lodgement_no || '—'} />
                <KV label="Condition report" value={formatDate(l.condition_report_at)} />
              </>
            ) : (
              <>
                <KV label="Ejari no." value={l.ejari_no || '—'} />
                <KV label="Ejari registered" value={formatDate(l.ejari_registered_date)} />
              </>
            )}
            <KV label="Move-in" value={formatDate(l.move_in_date)} />
            <KV label="Move-out" value={formatDate(l.move_out_date)} />
            <KV label="Unit" value={<Link component="button" variant="button" onClick={() => navigate(`/real-estate/units/${l.unit_id}`)}>{l.unit?.unit_number || l.unit_number || `#${l.unit_id}`}</Link>} />
            {l.notes && <MDTypography variant="caption" color="text" component="p" mt={1}>{l.notes}</MDTypography>}
          </InfoCard>
        </Grid>
      </Grid>

      <InfoCard icon="receipt" title={au ? 'Rent schedule' : 'Cheque schedule'} headerAction={<MDTypography variant="caption" color="text">{`${formatMoney(totals.cleared, cur, { decimals: 0 })} cleared of ${formatMoney(totals.all, cur, { decimals: 0 })}`}</MDTypography>}>
        <ReTable
          dense
          rows={schedule}
          emptyMessage={au ? 'No rent payments.' : 'No cheques.'}
          rowSx={(c) => (c.status === 'BOUNCED' ? { bgcolor: '#FFF5F4' } : {})}
          columns={[
            { key: 'sequence', label: '#', width: 36 },
            { key: 'due_date', label: 'Due', render: (c) => formatDate(c.due_date) },
            { key: 'amount', label: 'Amount', align: 'right', render: (c) => <Num>{formatMoney(c.amount, cur)}</Num>, footer: <Num bold>{formatMoney(round2(totals.all), cur)}</Num> },
            ...(au ? [] : [
              { key: 'cheque_no', label: 'Cheque no.', render: (c) => c.cheque_no || '—' },
              { key: 'bank_name', label: 'Bank', render: (c) => c.bank_name || '—' },
            ]),
            { key: 'dates', label: 'Deposited / cleared', render: (c) => [c.deposited_date && formatDate(c.deposited_date), c.cleared_date && formatDate(c.cleared_date)].filter(Boolean).join(' → ') || '—' },
            { key: 'status', label: 'Status', render: (c) => (
              <MDBox>
                <ReStatusChip vocab="cheque" status={c.status} size="xs" />
                {c.status === 'BOUNCED' && c.bounce_reason && <MDTypography variant="caption" color="error" display="block">{c.bounce_reason}</MDTypography>}
              </MDBox>
            ) },
            {
              key: 'actions', label: '', align: 'right',
              render: (c) => (canEdit ? (
                <MDBox display="flex" gap={0.5} justifyContent="flex-end">
                  {nextChequeActions(c.status).map((s) => (
                    <MDButton key={s} size="small" variant="text" color={s === 'BOUNCED' ? 'error' : 'info'} onClick={() => { setTarget(s); setCheque(c); }}>
                      {humanize(s)}
                    </MDButton>
                  ))}
                  <MDButton size="small" variant="text" color="secondary" onClick={() => { setTarget(null); setCheque(c); }}>Edit</MDButton>
                </MDBox>
              ) : null),
            },
          ]}
        />
      </InfoCard>

      {(l.commissions || []).length > 0 && (
        <MDBox mt={2}>
          <InfoCard icon="handshake" title="Commissions">
            <ReTable
              dense
              rows={l.commissions}
              columns={[
                { key: 'payee_name', label: 'Payee', render: (c) => c.payee_name || humanize(c.payee_type) },
                { key: 'gross_amount', label: 'Gross', align: 'right', render: (c) => <Num>{formatMoney(c.gross_amount, c.currency)}</Num> },
                { key: 'vat_amount', label: 'VAT', align: 'right', render: (c) => <Num>{formatMoney(c.vat_amount, c.currency)}</Num> },
                { key: 'total_amount', label: 'Total', align: 'right', render: (c) => <Num bold>{formatMoney(c.total_amount, c.currency)}</Num> },
                { key: 'status', label: 'Status', render: (c) => <ReStatusChip vocab="commission" status={c.status} size="xs" /> },
              ]}
            />
          </InfoCard>
        </MDBox>
      )}

      <ChequeDialog
        cheque={cheque ? { ...cheque, currency: cur, lease_number: l.lease_number } : null}
        targetStatus={target}
        onClose={() => setCheque(null)}
        onDone={(s) => { setCheque(null); notify(`${au ? 'Payment' : 'Cheque'} marked ${humanize(s).toLowerCase()}`); reload(); }}
      />
      <RenewLeaseDialog lease={l} open={renewOpen} onClose={() => setRenewOpen(false)} onDone={(nl) => { setRenewOpen(false); notify('Lease renewed'); navigate(`/real-estate/leases/${nl?.id || id}`); }} />
      <TerminateLeaseDialog lease={l} open={termOpen} onClose={() => setTermOpen(false)} onDone={() => { setTermOpen(false); notify('Lease terminated'); reload(); }} />
      <EjariDialog lease={l} open={ejariOpen} onClose={() => setEjariOpen(false)} onDone={() => { setEjariOpen(false); notify('Lease updated'); reload(); }} />
      {snack}
    </RePage>
  );
}
